import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtEuros, StatusBadge } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FilePlus2, FileText, ChevronRight, PencilLine, Trash2 } from "lucide-react";
import DuplicateReportButton from "../components/DuplicateReportButton";

export default function RequesterHome() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/expense-reports", { params: { mine: true } })
      .then(({ data }) => setReports(data.reports))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, []);

  const drafts = reports.filter((r) => r.status === "DRAFT");
  const inProgress = reports.filter((r) => r.status !== "DRAFT");

  const deleteDraft = async (e, r) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement le brouillon ${r.number} ?`)) return;
    try {
      await api.delete(`/expense-reports/${r.id}`);
      setReports((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Brouillon supprimé");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8 animate-enter" data-testid="requester-home">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>
          Bonjour {user?.first_name}
        </h1>
        <p className="text-lg text-slate-600 mt-1">Que souhaitez-vous faire ?</p>
      </div>

      <Link to="/nouvelle-note" data-testid="new-report-tile" className="block">
        <div className="bg-[#0369a1] hover:bg-[#075985] active:scale-[0.99] transition-all rounded-2xl p-6 sm:p-8 text-white flex items-center gap-5 shadow-md">
          <div className="bg-white/20 rounded-2xl p-4">
            <FilePlus2 className="h-10 w-10" aria-hidden="true" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <p className="text-2xl font-bold" style={{ fontFamily: "Work Sans" }}>Nouvelle note de frais</p>
            <p className="text-sky-100 text-lg">Déclarer une ou plusieurs dépenses</p>
          </div>
          <ChevronRight className="h-8 w-8" aria-hidden="true" strokeWidth={2.5} />
        </div>
      </Link>

      {drafts.length > 0 && (
        <section data-testid="drafts-section">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <PencilLine className="h-6 w-6 text-amber-600" aria-hidden="true" strokeWidth={2.5} />
            Mes brouillons à terminer
          </h2>
          <div className="space-y-3">
            {drafts.map((r) => (
              <Link key={r.id} to={`/nouvelle-note/${r.id}`} data-testid={`draft-card-${r.id}`} className="block">
                <div className="card-base !p-5 flex items-center gap-4 hover:border-amber-400 border-2 transition-colors">
                  <FileText className="h-8 w-8 text-amber-600 shrink-0" aria-hidden="true" strokeWidth={2.5} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-lg text-slate-900">{r.number}</p>
                    <p className="text-slate-600">Période {r.period} — {fmtEuros(r.total_requested_cents)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-[#b45309] font-bold text-lg whitespace-nowrap">Reprendre →</span>
                    <button onClick={(e) => deleteDraft(e, r)}
                      className="inline-flex items-center gap-1 px-3 h-10 rounded-lg border-2 border-red-300 text-[#b91c1c] font-semibold text-sm hover:bg-red-50"
                      data-testid={`delete-draft-${r.id}`}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section data-testid="my-reports-section">
        <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-4">Mes demandes</h2>
        {loading ? (
          <p className="text-slate-500 text-lg">Chargement…</p>
        ) : inProgress.length === 0 ? (
          <div className="card-base text-center py-10" data-testid="no-reports-message">
            <p className="text-lg text-slate-600">Vous n'avez pas encore envoyé de note de frais.</p>
            <p className="text-slate-500 mt-2">Commencez par « Nouvelle note de frais » ci-dessus.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {inProgress.map((r) => (
              <Link key={r.id} to={`/notes/${r.id}`} data-testid={`report-card-${r.id}`} className="block">
                <div className="card-base !p-5 hover:border-[#0369a1] border-2 transition-colors">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-bold text-lg text-slate-900">{r.number}</p>
                      <p className="text-slate-600">Période {r.period}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-xl text-slate-900">{fmtEuros(r.total_requested_cents)}</p>
                      {r.total_approved_cents > 0 && r.total_approved_cents !== r.total_requested_cents && (
                        <p className="text-sm text-green-700 font-semibold">Accepté : {fmtEuros(r.total_approved_cents)}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <StatusBadge status={r.status} label={r.status_label} />
                    <DuplicateReportButton report={r} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
